import {useRouter} from 'next/router'
import {useState, useEffect, useCallback, useMemo} from 'react'

function generateHash(str: string) {
    const hash = str.split(' ').join('_')
    return hash.startsWith('#') ? hash : `#${hash}`
}

/**
 * @caution iaw에서 이관 중, 외부 유입 로직을 유지하기 위해 보조적으로 호출합니다.
 *
 * 디자인시스템과 함께 쓸 때는 hash 기반 동작 대신 직접 open, close 함수를 상황에 맞게 호출해주세요.
 */
export default function useHash({hashId}: {hashId: string}) {
    const [hash, setHash] = useState(() => (typeof window !== 'undefined' ? window.location.hash : ''))
    const router = useRouter()

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function hashChangeHandler(url: string, callback: any) {
            const splitHash = url.split('#')[1]
            const RouterHash = splitHash ? '#' + splitHash : ''

            setHash(RouterHash)

            if (typeof callback === 'function') {
                callback?.()
            }
        }

        router.events.on('hashChangeStart', hashChangeHandler)
        return () => {
            router.events.off('hashChangeStart', hashChangeHandler)
        }
    }, [router.events])

    const HASH_ID = useMemo(() => generateHash(hashId), [hashId])

    function updateHash(
        {replace, hash: nextHash = HASH_ID}: {replace?: boolean; hash?: string} = {
            replace: false,
            hash: HASH_ID,
        },
    ) {
        const changedHash = nextHash.startsWith('#') ? nextHash : `#${nextHash}`
        if (changedHash !== hash) {
            if (replace) {
                router.replace(changedHash, undefined, {scroll: false, shallow: true})
            } else {
                router.push(changedHash, undefined, {scroll: false, shallow: true})
            }

            router.events.emit('hashChangeStart', changedHash)
        }
    }

    const hashFromUrl = useMemo(() => decodeURIComponent(hash), [hash])

    return {
        hash: hashFromUrl,
        isHashMatch: HASH_ID === hashFromUrl,
        updateHash: useCallback(updateHash, [HASH_ID, hash, router]),
        targetHashId: HASH_ID,
    }
}
